import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import {
  TruckIcon,
  ShieldCheckIcon,
  ClockIcon,
  SparklesIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  BuildingStorefrontIcon,
  ChevronRightIcon,
  HeartIcon,
  MapPinIcon,
  StarIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  ShoppingBagIcon,
  CubeIcon,
  BoltIcon,
  GiftIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Home = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // ── Shared cart from CartContext (same cart that Checkout.js reads) ──
  const { addToCart: ctxAddToCart, cartCount } = useCart();

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [addedMap, setAddedMap] = useState({});

  // ── Add to cart via context ──
  const handleAddToCart = useCallback((product, e) => {
    if (e) e.stopPropagation();
    // Pass priceValue as numeric `price` so CartContext getCartTotal() works
    ctxAddToCart({ ...product, price: product.priceValue });
    setAddedMap(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAddedMap(prev => ({ ...prev, [product.id]: false })), 1800);
    toast.success(`${product.name} added to cart! 🛒`);
  }, [ctxAddToCart]);

  // ── Buy Now: add to context cart, then go to checkout ──
  const handleBuyNow = useCallback((product, e) => {
    if (e) e.stopPropagation();
    ctxAddToCart({ ...product, price: product.priceValue });
    navigate('/checkout');
  }, [ctxAddToCart, navigate]);

  const testimonials = [
    { name: 'Priya Sharma', location: 'Palm Grove Apartments', rating: 5, text: 'Fresh milk every morning! The quality is unmatched and delivery is always on time.', image: '👩' },
    { name: 'Rahul Verma', location: 'Greenwood Society', rating: 5, text: 'Best dairy products I have found online. Pure, fresh and great prices!', image: '👨' },
    { name: 'Anita Desai', location: 'Lakeview Towers', rating: 5, text: 'The dairy products are always fresh. Love the 24/7 delivery availability!', image: '👩' },
  ];

  const offers = [
    { title: 'First Order', discount: '20% OFF', code: 'FRESH20', valid: 'Today only', icon: 0 },
    { title: 'Milk Subscription', discount: '15% OFF', code: 'MILK15', valid: 'Weekly', icon: 1 },
    { title: 'Combo Deal', discount: 'Eggs + Bread', price: '₹69', valid: 'Save ₹25', icon: 2 },
  ];

  const featuredProducts = [
    { id: 'p1', name: 'Fresh Cow Milk',    price: '₹25', priceValue: 25, originalPrice: '₹30', emoji: '🥛', badge: 'Bestseller',    desc: '500ml | Farm Fresh' },
    { id: 'p2', name: 'Farm Eggs (6 pcs)', price: '₹45', priceValue: 45, originalPrice: '₹55', emoji: '🥚', badge: 'Protein Rich',  desc: 'Free Range | Daily Fresh' },
    { id: 'p3', name: 'White Butter',      price: '₹50', priceValue: 50, originalPrice: '₹60', emoji: '🧈', badge: 'Pure',          desc: '100g | Creamy' },
    { id: 'p4', name: 'Cheese Block',      price: '₹80', priceValue: 80, originalPrice: '₹95', emoji: '🧀', badge: 'Premium',       desc: '200g | Processed' },
    { id: 'p5', name: 'Fresh Curd',        price: '₹30', priceValue: 30, originalPrice: '₹35', emoji: '🥛', badge: 'Probiotic',     desc: '400g | Thick Set' },
    { id: 'p6', name: 'Buttermilk',        price: '₹20', priceValue: 20, originalPrice: '₹25', emoji: '🧃', badge: 'Refreshing',    desc: '500ml | Chilled' },
    { id: 'p7', name: 'Ice Cream',         price: '₹60', priceValue: 60, originalPrice: '₹75', emoji: '🍦', badge: 'Treat',         desc: '100ml | Vanilla' },
    { id: 'p8', name: 'Flavored Milk',     price: '₹35', priceValue: 35, originalPrice: '₹40', emoji: '🥛', badge: "Kids' Choice", desc: '200ml | Chocolate' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Lora:ital,wght@0,400;0,600;1,400&display=swap');

    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Nunito', sans-serif;
      color: #1a2e1a;
      overflow-x: hidden;
      background: #fff;
    }

    /* ── CONTAINER ── */
    .fh-container {
      width: 100%;
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 1rem;
    }
    @media(min-width:640px){ .fh-container{ padding: 0 1.5rem; } }
    @media(min-width:1024px){ .fh-container{ padding: 0 2rem; } }

    /* ── HEADER ── */
    .fh-header {
      position: sticky;
      top: 0;
      z-index: 50;
      width: 100%;
      transition: all 0.35s ease;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(229,231,235,0.6);
    }
    .fh-header.scrolled {
      box-shadow: 0 4px 20px rgba(0,0,0,0.07);
      border-bottom-color: rgba(5,150,105,0.15);
    }

    .fh-header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 60px;
      gap: 0.5rem;
    }
    @media(min-width:768px){ .fh-header-inner{ height: 72px; } }

    /* Logo */
    .fh-logo {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;
      cursor: pointer;
    }
    .fh-logo img {
      height: 36px;
      width: auto;
      object-fit: contain;
    }
    @media(min-width:768px){ .fh-logo img{ height: 44px; } }

    .fh-logo-text h1 {
      font-size: 1rem;
      font-weight: 800;
      background: linear-gradient(135deg, #059669, #0891b2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.1;
      white-space: nowrap;
    }
    @media(min-width:480px){ .fh-logo-text h1{ font-size: 1.15rem; } }
    @media(min-width:768px){ .fh-logo-text h1{ font-size: 1.4rem; } }

    .fh-logo-text span {
      display: none;
      font-size: 0.65rem;
      color: #6b7280;
      letter-spacing: 0.3px;
    }
    @media(min-width:480px){ .fh-logo-text span{ display: block; } }

    /* Desktop Nav */
    .fh-nav {
      display: none;
    }
    @media(min-width:768px){
      .fh-nav {
        display: flex;
        align-items: center;
        gap: 1.75rem;
      }
    }
    .fh-nav-link {
      color: #374151;
      font-weight: 600;
      font-size: 0.9rem;
      text-decoration: none;
      position: relative;
      transition: color 0.2s;
      white-space: nowrap;
    }
    .fh-nav-link:hover { color: #059669; }
    .fh-nav-link::after {
      content: '';
      position: absolute;
      bottom: -3px; left: 0;
      width: 0; height: 2px;
      background: #059669;
      transition: width 0.3s;
    }
    .fh-nav-link:hover::after { width: 100%; }

    /* Header Actions */
    .fh-header-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .fh-icon-btn {
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%;
      border: none;
      background: #f3f4f6;
      color: #374151;
      cursor: pointer;
      transition: all 0.2s;
      flex-shrink: 0;
    }
    .fh-icon-btn:hover { background: #e5e7eb; color: #059669; }
    @media(min-width:768px){ .fh-icon-btn{ width: 40px; height: 40px; } }

    .fh-shop-btn {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.45rem 1rem;
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      font-weight: 700;
      font-size: 0.82rem;
      border-radius: 9999px;
      border: none;
      cursor: pointer;
      transition: all 0.3s;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .fh-shop-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 12px rgba(16,185,129,0.3);
    }
    @media(min-width:768px){
      .fh-shop-btn {
        padding: 0.55rem 1.4rem;
        font-size: 0.9rem;
      }
    }

    /* Mobile Menu Toggle */
    .fh-menu-toggle {
      display: flex;
      @media(min-width:768px){ display: none; }
    }
    @media(min-width:768px){ .fh-menu-toggle{ display: none; } }

    /* Mobile Drawer */
    .fh-mobile-menu {
      position: fixed;
      inset: 0;
      z-index: 100;
      pointer-events: none;
    }
    .fh-mobile-menu.open { pointer-events: all; }

    .fh-mobile-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0);
      transition: background 0.3s;
    }
    .fh-mobile-menu.open .fh-mobile-overlay {
      background: rgba(0,0,0,0.4);
    }

    .fh-mobile-drawer {
      position: absolute;
      top: 0; right: 0;
      width: min(280px, 85vw);
      height: 100%;
      background: white;
      transform: translateX(100%);
      transition: transform 0.35s cubic-bezier(0.4,0,0.2,1);
      display: flex;
      flex-direction: column;
      box-shadow: -8px 0 30px rgba(0,0,0,0.1);
    }
    .fh-mobile-menu.open .fh-mobile-drawer {
      transform: translateX(0);
    }

    .fh-mobile-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid #f3f4f6;
    }

    .fh-mobile-nav {
      flex: 1;
      padding: 1rem 0;
      overflow-y: auto;
    }

    .fh-mobile-link {
      display: flex;
      align-items: center;
      padding: 0.85rem 1.5rem;
      font-size: 1rem;
      font-weight: 600;
      color: #374151;
      text-decoration: none;
      border-left: 3px solid transparent;
      transition: all 0.2s;
    }
    .fh-mobile-link:hover {
      background: #f0fdf4;
      color: #059669;
      border-left-color: #059669;
    }

    /* ── HERO ── */
    .fh-hero {
      position: relative;
      background: linear-gradient(150deg, #f0fdf4 0%, #ffffff 50%, #f0f9ff 100%);
      padding: 2.5rem 0 3rem;
      overflow: hidden;
    }
    @media(min-width:768px){ .fh-hero{ padding: 4rem 0 5rem; } }

    .fh-hero-grid {
      display: grid;
      gap: 2.5rem;
      align-items: center;
    }
    @media(min-width:1024px){
      .fh-hero-grid {
        grid-template-columns: 1fr 1fr;
        gap: 4rem;
      }
    }

    /* Decorative blobs */
    .fh-blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(70px);
      pointer-events: none;
      z-index: 0;
    }
    .fh-blob-1 {
      width: 22rem; height: 22rem;
      background: #a7f3d0;
      opacity: 0.18;
      top: -4rem; left: -4rem;
      animation: blobMove 18s ease-in-out infinite alternate;
    }
    .fh-blob-2 {
      width: 18rem; height: 18rem;
      background: #99f6e4;
      opacity: 0.14;
      bottom: -3rem; right: -3rem;
      animation: blobMove 22s ease-in-out infinite alternate-reverse;
    }
    @keyframes blobMove {
      from { transform: translate(0,0) scale(1); }
      to   { transform: translate(30px,-40px) scale(1.12); }
    }

    .fh-hero-content { position: relative; z-index: 2; }

    .fh-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background: #ecfdf5;
      border: 1px solid #a7f3d0;
      color: #047857;
      font-size: 0.78rem;
      font-weight: 700;
      padding: 0.35rem 1rem;
      border-radius: 9999px;
      margin-bottom: 1.25rem;
      letter-spacing: 0.5px;
    }

    .fh-hero-title {
      font-family: 'Nunito', sans-serif;
      font-size: 2.2rem;
      font-weight: 900;
      line-height: 1.15;
      margin-bottom: 1.1rem;
      color: #111827;
    }
    @media(min-width:640px){ .fh-hero-title{ font-size: 2.8rem; } }
    @media(min-width:1024px){ .fh-hero-title{ font-size: 3.4rem; } }

    .fh-gradient-text {
      background: linear-gradient(135deg, #059669, #0891b2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .fh-hero-subtitle {
      font-size: 1.05rem;
      color: #4b5563;
      line-height: 1.65;
      margin-bottom: 2rem;
      max-width: 34rem;
    }
    @media(min-width:768px){ .fh-hero-subtitle{ font-size: 1.15rem; } }

    .fh-btn-group {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-bottom: 2.25rem;
    }

    .fh-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.7rem 1.5rem;
      border-radius: 9999px;
      font-weight: 700;
      font-size: 0.9rem;
      border: none;
      cursor: pointer;
      transition: all 0.3s;
      white-space: nowrap;
    }
    @media(min-width:640px){
      .fh-btn { padding: 0.8rem 1.75rem; font-size: 0.95rem; }
    }

    .fh-btn-primary {
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      box-shadow: 0 4px 14px rgba(16,185,129,0.3);
    }
    .fh-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(16,185,129,0.35);
    }

    .fh-btn-outline {
      background: white;
      color: #059669;
      border: 2px solid #d1fae5;
    }
    .fh-btn-outline:hover {
      border-color: #10b981;
      background: #f0fdf4;
      transform: translateY(-2px);
    }

    /* Hero Stats */
    .fh-hero-stats {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 1.5rem;
    }
    @media(min-width:640px){ .fh-hero-stats{ gap: 2rem; } }

    .fh-stat-num {
      font-size: 1.6rem;
      font-weight: 900;
      color: #111827;
      line-height: 1;
    }
    .fh-stat-lbl {
      font-size: 0.72rem;
      color: #6b7280;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      margin-top: 0.2rem;
    }
    .fh-stat-divider {
      width: 1px; height: 2rem;
      background: #e5e7eb;
    }

    /* Hero Image Panel */
    .fh-hero-panel {
      background: white;
      border-radius: 2rem;
      box-shadow: 0 20px 50px -10px rgba(0,0,0,0.12);
      overflow: hidden;
      border: 1px solid #f3f4f6;
    }

    .fh-hero-panel-header {
      background: linear-gradient(135deg, #059669, #0891b2);
      padding: 1.25rem 1.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .fh-hero-panel-title {
      color: white;
      font-weight: 800;
      font-size: 1rem;
    }
    .fh-hero-panel-badge {
      background: rgba(255,255,255,0.25);
      color: white;
      font-size: 0.7rem;
      font-weight: 700;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
    }

    .fh-panel-products {
      padding: 1rem;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }

    .fh-panel-product {
      background: #f9fafb;
      border-radius: 1rem;
      padding: 1rem;
      text-align: center;
      border: 1px solid #f3f4f6;
      transition: all 0.25s;
      cursor: pointer;
    }
    .fh-panel-product:hover {
      background: #f0fdf4;
      border-color: #a7f3d0;
      transform: translateY(-2px);
    }
    .fh-panel-emoji { font-size: 2rem; margin-bottom: 0.4rem; }
    .fh-panel-name { font-size: 0.78rem; font-weight: 700; color: #111827; margin-bottom: 0.15rem; }
    .fh-panel-price { font-size: 0.9rem; font-weight: 800; color: #059669; }

    .fh-panel-footer {
      padding: 1rem 1.25rem;
      border-top: 1px solid #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .fh-panel-footer-text { font-size: 0.8rem; color: #6b7280; }

    /* ── TRUST BAR ── */
    .fh-trust-bar {
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
      padding: 1rem 0;
    }
    .fh-trust-items {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 1.25rem 2rem;
    }
    .fh-trust-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .fh-trust-icon {
      width: 1.75rem; height: 1.75rem;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
    }
    .fh-trust-item span { font-size: 0.82rem; font-weight: 600; color: #374151; }

    /* ── SECTION COMMONS ── */
    .fh-section { padding: 4rem 0; }
    @media(min-width:768px){ .fh-section{ padding: 5.5rem 0; } }

    .fh-section-head { text-align: center; max-width: 44rem; margin: 0 auto 3rem; }

    .fh-section-badge {
      display: inline-block;
      font-size: 0.78rem;
      font-weight: 700;
      padding: 0.35rem 1rem;
      border-radius: 9999px;
      margin-bottom: 0.75rem;
      letter-spacing: 0.5px;
    }
    .fh-section-badge-green { background: #ecfdf5; color: #047857; }
    .fh-section-badge-amber { background: #fef3c7; color: #d97706; }
    .fh-section-badge-blue  { background: #ecfeff; color: #0891b2; }
    .fh-section-badge-purple{ background: #faf5ff; color: #7c3aed; }

    .fh-section-title {
      font-size: 1.9rem;
      font-weight: 900;
      color: #111827;
      line-height: 1.2;
      margin-bottom: 0.75rem;
    }
    @media(min-width:768px){ .fh-section-title{ font-size: 2.4rem; } }

    .fh-section-sub {
      font-size: 1.05rem;
      color: #6b7280;
      line-height: 1.6;
    }

    /* ── FEATURED PRODUCTS ── */
    .fh-products-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }
    @media(min-width:640px){ .fh-products-grid{ grid-template-columns: repeat(3, 1fr); gap: 1.25rem; } }
    @media(min-width:1024px){ .fh-products-grid{ grid-template-columns: repeat(4, 1fr); gap: 1.5rem; } }

    .fh-product-card {
      background: white;
      border: 1px solid #f3f4f6;
      border-radius: 1.25rem;
      overflow: hidden;
      transition: all 0.3s;
      cursor: pointer;
    }
    .fh-product-card:hover {
      border-color: #a7f3d0;
      box-shadow: 0 12px 30px rgba(5,150,105,0.1);
      transform: translateY(-4px);
    }

    .fh-product-img {
      background: linear-gradient(135deg, #f0fdf4, #f0f9ff);
      padding: 1.5rem;
      text-align: center;
      font-size: 3rem;
      position: relative;
    }

    .fh-product-badge-pill {
      position: absolute;
      top: 0.6rem; left: 0.6rem;
      background: #f59e0b;
      color: white;
      font-size: 0.62rem;
      font-weight: 700;
      padding: 0.2rem 0.55rem;
      border-radius: 9999px;
    }

    .fh-product-body { padding: 0.9rem 1rem 1rem; }
    .fh-product-name { font-size: 0.88rem; font-weight: 700; color: #111827; margin-bottom: 0.2rem; }
    .fh-product-desc { font-size: 0.72rem; color: #9ca3af; margin-bottom: 0.6rem; }

    .fh-product-pricing {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      margin-bottom: 0.7rem;
    }
    .fh-product-price { font-size: 1rem; font-weight: 800; color: #059669; }
    .fh-product-original { font-size: 0.78rem; color: #9ca3af; text-decoration: line-through; }

    /* Two action buttons per card */
    .fh-card-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.4rem;
      margin-top: 0.1rem;
    }

    .fh-add-btn {
      padding: 0.5rem 0.25rem;
      background: #f0fdf4;
      color: #059669;
      font-weight: 700;
      font-size: 0.76rem;
      border: 1.5px solid #d1fae5;
      border-radius: 0.6rem;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .fh-add-btn:hover { background: #059669; color: white; border-color: #059669; }
    .fh-add-btn.added { background: #047857; color: white; border-color: #047857; }

    .fh-buy-btn {
      padding: 0.5rem 0.25rem;
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      font-weight: 700;
      font-size: 0.76rem;
      border: none;
      border-radius: 0.6rem;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .fh-buy-btn:hover {
      background: linear-gradient(135deg, #059669, #047857);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(5,150,105,0.3);
    }

    /* Cart badge on header icon */
    .fh-cart-wrap { position: relative; display: inline-flex; }
    .fh-cart-badge {
      position: absolute;
      top: -5px; right: -5px;
      width: 1.1rem; height: 1.1rem;
      background: #ef4444;
      color: white;
      font-size: 0.6rem;
      font-weight: 800;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid white;
      pointer-events: none;
    }

    /* ── USP ── */
    .fh-usp-section {
      background: linear-gradient(145deg, #f3faf7, #fafffe);
      padding: 4rem 0;
    }
    .fh-usp-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1.25rem;
      margin-top: 2.5rem;
    }
    @media(min-width:640px){ .fh-usp-grid{ grid-template-columns: repeat(2,1fr); } }
    @media(min-width:1024px){ .fh-usp-grid{ grid-template-columns: repeat(4,1fr); gap: 1.5rem; } }

    .fh-usp-card {
      background: white;
      padding: 1.75rem 1.5rem;
      border-radius: 1.5rem;
      border: 1px solid rgba(5,150,105,0.08);
      text-align: center;
      transition: all 0.35s cubic-bezier(0.175,0.885,0.32,1.275);
      position: relative;
      overflow: hidden;
    }
    .fh-usp-card::before {
      content: '';
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 3px;
      background: linear-gradient(90deg, #10b981, #06b6d4);
      opacity: 0;
      transition: opacity 0.3s;
    }
    .fh-usp-card:hover::before { opacity: 1; }
    .fh-usp-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 30px -8px rgba(5,150,105,0.12);
    }

    .fh-usp-icon-wrap {
      width: 4rem; height: 4rem;
      background: #ecfdf5;
      border-radius: 1rem;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 1rem;
      transition: all 0.3s;
    }
    .fh-usp-card:hover .fh-usp-icon-wrap {
      background: #d1fae5;
      transform: scale(1.1) rotate(4deg);
    }
    .fh-usp-icon-wrap svg { width: 2rem; height: 2rem; color: #059669; }
    .fh-usp-title { font-size: 1.05rem; font-weight: 800; color: #111827; margin-bottom: 0.4rem; }
    .fh-usp-desc  { font-size: 0.85rem; color: #6b7280; line-height: 1.5; }

    /* ── OFFERS ── */
    .fh-offers-section { background: linear-gradient(135deg, #fffbeb, #fef3c7); }

    .fh-offers-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1.25rem;
    }
    @media(min-width:640px){ .fh-offers-grid{ grid-template-columns: repeat(2,1fr); } }
    @media(min-width:1024px){ .fh-offers-grid{ grid-template-columns: repeat(3,1fr); } }

    .fh-offer-card {
      background: white;
      border-radius: 1.5rem;
      padding: 1.5rem;
      box-shadow: 0 4px 16px rgba(0,0,0,0.04);
      border: 1px solid #fde68a;
      display: flex;
      align-items: center;
      gap: 1rem;
      transition: all 0.3s;
    }
    .fh-offer-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 24px rgba(245,158,11,0.12);
    }

    .fh-offer-icon-box {
      width: 3.25rem; height: 3.25rem;
      background: #fef3c7;
      border-radius: 1rem;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .fh-offer-icon-box svg { width: 1.5rem; height: 1.5rem; color: #d97706; }

    .fh-offer-title    { font-size: 1rem; font-weight: 700; color: #111827; margin-bottom: 0.2rem; }
    .fh-offer-discount { font-size: 1.4rem; font-weight: 900; color: #d97706; line-height: 1; margin-bottom: 0.3rem; }
    .fh-offer-code     {
      display: inline-block;
      font-size: 0.72rem; color: #6b7280;
      background: #f3f4f6;
      padding: 0.2rem 0.6rem;
      border-radius: 9999px;
      margin-bottom: 0.2rem;
    }
    .fh-offer-valid { font-size: 0.72rem; color: #9ca3af; }

    /* ── HOW IT WORKS ── */
    .fh-steps-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1.5rem;
      margin-top: 3rem;
    }
    @media(min-width:768px){ .fh-steps-grid{ grid-template-columns: repeat(3,1fr); gap: 2rem; } }

    .fh-step-card {
      text-align: center;
      padding: 2rem 1.5rem;
      background: linear-gradient(145deg, #fff, #f9fafb);
      border-radius: 2rem;
      border: 1px solid #f3f4f6;
      transition: all 0.35s;
    }
    .fh-step-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 28px -8px rgba(5,150,105,0.1);
      border-color: rgba(5,150,105,0.2);
    }

    .fh-step-num {
      width: 4.5rem; height: 4.5rem;
      border-radius: 1.25rem;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 1.25rem;
      font-size: 1.75rem;
      font-weight: 900;
      color: white;
      box-shadow: 0 8px 16px -4px rgba(5,150,105,0.3);
      transition: all 0.3s;
    }
    .fh-step-card:hover .fh-step-num { transform: scale(1.08) rotate(4deg); }

    .fh-step-card h3 { font-size: 1.2rem; font-weight: 800; color: #111827; margin-bottom: 0.6rem; }
    .fh-step-card p  { font-size: 0.9rem; color: #6b7280; line-height: 1.6; }

    /* ── STATS BANNER ── */
    .fh-stats-banner {
      background: linear-gradient(135deg, #0b4f6c, #0a5c6e);
      padding: 3rem 0;
      color: white;
    }
    .fh-stats-grid {
      display: grid;
      grid-template-columns: repeat(2,1fr);
      gap: 1.5rem;
      text-align: center;
    }
    @media(min-width:768px){ .fh-stats-grid{ grid-template-columns: repeat(4,1fr); } }

    .fh-stat-banner-num {
      font-size: 2.25rem;
      font-weight: 900;
      background: linear-gradient(135deg, #fff, #e0f2fe);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1;
      margin-bottom: 0.4rem;
    }
    .fh-stat-banner-lbl { font-size: 0.88rem; opacity: 0.85; letter-spacing: 0.5px; }

    /* ── TESTIMONIALS ── */
    .fh-testimonials-section { background: #f9fafb; }

    .fh-testimonial-card {
      background: white;
      border-radius: 2rem;
      padding: 2rem;
      border: 1px solid #f3f4f6;
      max-width: 44rem;
      margin: 0 auto;
      box-shadow: 0 8px 24px rgba(0,0,0,0.04);
    }

    .fh-testimonial-head {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1.25rem;
    }
    .fh-testimonial-avatar {
      width: 3.25rem; height: 3.25rem;
      background: #e0f2fe;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.6rem;
    }
    .fh-testimonial-name { font-weight: 800; color: #111827; margin-bottom: 0.15rem; }
    .fh-testimonial-loc  { font-size: 0.8rem; color: #6b7280; display: flex; align-items: center; gap: 0.25rem; }
    .fh-stars { display: flex; gap: 0.2rem; color: #fbbf24; margin-top: 0.25rem; }
    .fh-stars svg { width: 0.9rem; height: 0.9rem; fill: currentColor; }

    .fh-testimonial-text {
      font-size: 1.05rem;
      color: #374151;
      line-height: 1.65;
      font-style: italic;
    }

    .fh-dots {
      display: flex;
      justify-content: center;
      gap: 0.4rem;
      margin-top: 1.75rem;
    }
    .fh-dot {
      width: 0.5rem; height: 0.5rem;
      border-radius: 9999px;
      border: none;
      cursor: pointer;
      transition: all 0.3s;
    }

    /* ── SUSTAINABILITY ── */
    .fh-sustainability-grid {
      display: grid;
      gap: 2.5rem;
      align-items: center;
    }
    @media(min-width:1024px){ .fh-sustainability-grid{ grid-template-columns: 1fr 1fr; } }

    .fh-sustainability-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-top: 1.5rem;
    }
    .fh-sus-badge {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #ecfdf5;
      padding: 0.5rem 1rem;
      border-radius: 9999px;
      font-size: 0.85rem;
      font-weight: 700;
      color: #047857;
    }
    .fh-sus-badge svg { width: 1rem; height: 1rem; }

    .fh-sus-visual {
      background: #f0fdf4;
      border-radius: 2rem;
      padding: 2.5rem;
      text-align: center;
    }

    /* ── APP SECTION ── */
    .fh-app-section {
      background: linear-gradient(135deg, #059669, #0891b2);
      padding: 4rem 0;
      overflow: hidden;
    }
    @media(min-width:768px){ .fh-app-section{ padding: 5rem 0; } }

    .fh-app-grid {
      display: grid;
      gap: 2.5rem;
      align-items: center;
    }
    @media(min-width:1024px){ .fh-app-grid{ grid-template-columns: 1fr 1fr; gap: 4rem; } }

    .fh-app-title {
      font-size: 2rem;
      font-weight: 900;
      color: white;
      line-height: 1.2;
      margin-bottom: 1rem;
    }
    @media(min-width:768px){ .fh-app-title{ font-size: 2.6rem; } }

    .fh-app-text {
      color: rgba(255,255,255,0.9);
      font-size: 1.05rem;
      line-height: 1.65;
      margin-bottom: 2rem;
    }

    .fh-app-btns {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }
    .fh-app-store-btn {
      background: #111827;
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 9999px;
      font-weight: 700;
      font-size: 0.9rem;
      display: flex; align-items: center; gap: 0.6rem;
      border: none; cursor: pointer;
      transition: all 0.3s;
    }
    .fh-app-store-btn:hover { background: #1f2937; transform: translateY(-2px); box-shadow: 0 8px 16px rgba(0,0,0,0.25); }
    .fh-app-store-btn svg { width: 1.1rem; height: 1.1rem; }

    .fh-app-visual {
      background: rgba(255,255,255,0.12);
      backdrop-filter: blur(12px);
      border-radius: 2rem;
      padding: 2rem;
      border: 1px solid rgba(255,255,255,0.2);
      max-width: 26rem;
      margin: 0 auto;
      transform: rotate(2deg);
    }
    .fh-app-inner {
      background: white;
      border-radius: 1.5rem;
      padding: 1.75rem;
      text-align: center;
    }
    .fh-app-emojis { font-size: 2.5rem; display: flex; justify-content: center; gap: 0.75rem; margin-bottom: 1rem; }

    /* ── FOOTER ── */
    .fh-footer {
      background: #0f172a;
      color: white;
      padding: 4rem 0 2rem;
    }

    .fh-footer-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 2.5rem;
      margin-bottom: 3rem;
    }
    @media(min-width:640px){ .fh-footer-grid{ grid-template-columns: repeat(2,1fr); } }
    @media(min-width:1024px){ .fh-footer-grid{ grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 3rem; } }

    .fh-footer-logo {
      display: flex; align-items: center; gap: 0.75rem;
      margin-bottom: 1.25rem;
    }
    .fh-footer-logo img { height: 44px; }
    .fh-footer-logo h3 {
      font-size: 1.35rem; font-weight: 800;
      background: linear-gradient(135deg, #34d399, #22d3ee);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .fh-footer-about { color: #94a3b8; line-height: 1.7; font-size: 0.9rem; margin-bottom: 1.5rem; }

    .fh-social-row { display: flex; gap: 0.75rem; }
    .fh-social-btn {
      width: 2.25rem; height: 2.25rem;
      background: #1e293b;
      border-radius: 0.65rem;
      display: flex; align-items: center; justify-content: center;
      color: white;
      transition: all 0.3s;
      text-decoration: none;
    }
    .fh-social-btn:hover { background: #2563eb; transform: translateY(-2px); }
    .fh-social-btn svg { width: 1.1rem; height: 1.1rem; }

    .fh-footer-col-title {
      font-size: 1rem; font-weight: 700;
      color: white;
      margin-bottom: 1.25rem;
      display: flex; align-items: center; gap: 0.4rem;
    }
    .fh-footer-col-title svg { width: 1rem; height: 1rem; color: #ef4444; }

    .fh-footer-links { list-style: none; }
    .fh-footer-links li { margin-bottom: 0.75rem; }
    .fh-footer-links a {
      color: #94a3b8;
      text-decoration: none;
      font-size: 0.9rem;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
    }
    .fh-footer-links a svg { width: 0.7rem; height: 0.7rem; }
    .fh-footer-links a:hover { color: white; transform: translateX(4px); }

    .fh-footer-contact-list { list-style: none; }
    .fh-footer-contact-list li {
      display: flex; align-items: flex-start; gap: 0.65rem;
      color: #94a3b8; font-size: 0.9rem;
      margin-bottom: 0.85rem;
    }
    .fh-footer-contact-list li svg { width: 1.1rem; height: 1.1rem; flex-shrink: 0; margin-top: 0.1rem; }

    .fh-newsletter { margin-top: 1.5rem; }
    .fh-newsletter-label { font-size: 0.9rem; font-weight: 600; color: white; margin-bottom: 0.75rem; }
    .fh-newsletter-row { display: flex; gap: 0.4rem; }
    .fh-newsletter-row input {
      flex: 1;
      padding: 0.65rem 1rem;
      border-radius: 9999px;
      border: 1px solid #334155;
      background: #1e293b;
      color: white;
      font-size: 0.85rem;
      min-width: 0;
    }
    .fh-newsletter-row input::placeholder { color: #64748b; }
    .fh-newsletter-btn {
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 9999px;
      padding: 0.65rem 1.1rem;
      font-weight: 700;
      font-size: 0.82rem;
      cursor: pointer;
      transition: all 0.3s;
      white-space: nowrap;
    }
    .fh-newsletter-btn:hover { background: #1d4ed8; transform: translateY(-1px); }

    .fh-footer-bottom {
      border-top: 1px solid #1e293b;
      padding-top: 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      text-align: center;
    }
    @media(min-width:768px){
      .fh-footer-bottom {
        flex-direction: row;
        justify-content: space-between;
        text-align: left;
      }
    }

    .fh-copyright { color: #64748b; font-size: 0.82rem; }
    .fh-footer-bottom-links { display: flex; flex-wrap: wrap; gap: 1.5rem; justify-content: center; }
    .fh-footer-bottom-links a {
      color: #94a3b8; text-decoration: none; font-size: 0.82rem;
      transition: color 0.2s;
    }
    .fh-footer-bottom-links a:hover { color: white; }

    .fh-seo-link {
      color: #f59e0b !important;
      font-weight: 700;
      display: inline-flex; align-items: center; gap: 0.35rem;
    }
    .fh-seo-link:hover { color: #fbbf24 !important; }

    @keyframes heartbeat {
      0%,100% { transform: scale(1); }
      25%,75%  { transform: scale(1.15); }
    }
    .animate-heart { animation: heartbeat 1.4s ease-in-out infinite; }

    /* Utils */
    .flex       { display: flex; }
    .items-center { align-items: center; }
    .justify-between { justify-content: space-between; }
    .text-center { text-align: center; }
  `;

  return (
    <>
      <style>{styles}</style>
      <div>
        {/* ── HEADER ── */}
        <header className={`fh-header${scrolled ? ' scrolled' : ''}`}>
          <div className="fh-container">
            <div className="fh-header-inner">
              {/* Logo */}
              <div className="fh-logo" onClick={() => navigate('/')}>
                <img
                  src="/images/logo.png"
                  alt="Fresh Hills"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }}
                />
                <div className="fh-logo-text">
                  <h1>Fresh Hills</h1>
                  <span>Fresh & Healthy Since 2024</span>
                </div>
              </div>

              {/* Desktop Nav */}
              <nav className="fh-nav">
                <a href="#home"         className="fh-nav-link">Home</a>
                <button onClick={() => navigate('/products')} className="fh-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Products</button>
                <a href="#offers"       className="fh-nav-link">Offers</a>
                <a href="#how-it-works" className="fh-nav-link">How It Works</a>
                <a href="#contact"      className="fh-nav-link">Contact</a>
              </nav>

              {/* Actions */}
              <div className="fh-header-actions">
                <button className="fh-icon-btn" title="Search">
                  <MagnifyingGlassIcon style={{ width: '1.1rem', height: '1.1rem' }} />
                </button>
                <div className="fh-cart-wrap">
                  <button className="fh-icon-btn" title="Cart" onClick={() => navigate('/products')}>
                    <ShoppingBagIcon style={{ width: '1.1rem', height: '1.1rem' }} />
                  </button>
                  {cartCount > 0 && <span className="fh-cart-badge">{cartCount > 9 ? '9+' : cartCount}</span>}
                </div>
                <button className="fh-shop-btn" onClick={() => navigate('/products')}>
                  <ShoppingBagIcon style={{ width: '0.95rem', height: '0.95rem' }} />
                  <span>Shop Now</span>
                </button>
                {/* Hamburger */}
                <button
                  className="fh-icon-btn fh-menu-toggle"
                  style={{ display: 'flex' }}
                  onClick={() => setMobileMenuOpen(true)}
                  title="Menu"
                >
                  <Bars3Icon style={{ width: '1.2rem', height: '1.2rem' }} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Drawer */}
        <div className={`fh-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
          <div className="fh-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
          <div className="fh-mobile-drawer">
            <div className="fh-mobile-header">
              <div className="fh-logo">
                <img src="/images/logo.png" alt="Fresh Hills"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/36?text=FH'; }}
                  style={{ height: '32px' }}
                />
                <div className="fh-logo-text">
                  <h1>Fresh Hills</h1>
                </div>
              </div>
              <button className="fh-icon-btn" onClick={() => setMobileMenuOpen(false)}>
                <XMarkIcon style={{ width: '1.2rem', height: '1.2rem' }} />
              </button>
            </div>
            <nav className="fh-mobile-nav">
              {[
                { label: 'Home',         href: '#home',        route: null },
                { label: 'Products',     href: null,           route: '/products' },
                { label: 'Offers',       href: '#offers',      route: null },
                { label: 'How It Works', href: '#how-it-works',route: null },
                { label: 'Contact',      href: '#contact',     route: null },
              ].map((item) => (
                item.route
                  ? (
                    <button
                      key={item.label}
                      className="fh-mobile-link"
                      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      onClick={() => { setMobileMenuOpen(false); navigate(item.route); }}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      className="fh-mobile-link"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  )
              ))}
            </nav>
            <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #f3f4f6' }}>
              <button
                className="fh-shop-btn"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setMobileMenuOpen(false); navigate('/products'); }}
              >
                <ShoppingBagIcon style={{ width: '1rem', height: '1rem' }} />
                Shop Now
              </button>
            </div>
          </div>
        </div>

        {/* ── HERO ── */}
        <section id="home" className="fh-hero">
          <div className="fh-blob fh-blob-1" />
          <div className="fh-blob fh-blob-2" />
          <div className="fh-container fh-hero-content">
            <div className="fh-hero-grid">
              {/* Left */}
              <div>
                <div className="fh-badge">🌿 100% Farm Fresh · No Preservatives</div>
                <h1 className="fh-hero-title">
                  <span className="fh-gradient-text">Fresh Dairy</span>
                  <br />Delivered to Your Door
                </h1>
                <p className="fh-hero-subtitle">
                  India's most trusted online dairy store. Pure cow milk, farm eggs, creamy butter and more — 
                  delivered fresh to your home every morning.
                </p>
                <div className="fh-btn-group">
                  <button className="fh-btn fh-btn-primary" onClick={() => navigate('/products')}>
                    <ShoppingBagIcon style={{ width: '1.1rem', height: '1.1rem' }} />
                    Shop Products
                    <ArrowRightIcon style={{ width: '0.9rem', height: '0.9rem' }} />
                  </button>
                  <button className="fh-btn fh-btn-outline" onClick={() => navigate('/products')}>
                    <TruckIcon style={{ width: '1.1rem', height: '1.1rem' }} />
                    Order Delivery
                  </button>
                </div>
                <div className="fh-hero-stats">
                  <div>
                    <div className="fh-stat-num">50+</div>
                    <div className="fh-stat-lbl">Dairy Products</div>
                  </div>
                  <div className="fh-stat-divider" />
                  <div>
                    <div className="fh-stat-num">10k+</div>
                    <div className="fh-stat-lbl">Happy Customers</div>
                  </div>
                  <div className="fh-stat-divider" />
                  <div>
                    <div className="fh-stat-num">24/7</div>
                    <div className="fh-stat-lbl">Delivery</div>
                  </div>
                </div>
              </div>

              {/* Right – Product Preview Card */}
              <div className="fh-hero-panel">
                <div className="fh-hero-panel-header">
                  <span className="fh-hero-panel-title">🥛 Today's Fresh Stock</span>
                  <span className="fh-hero-panel-badge">● Live Stock</span>
                </div>
                <div className="fh-panel-products">
                  {featuredProducts.slice(0, 4).map((p) => (
                    <div
                      key={p.name}
                      className="fh-panel-product"
                      onClick={() => navigate('/products')}
                    >
                      <div className="fh-panel-emoji">{p.emoji}</div>
                      <div className="fh-panel-name">{p.name}</div>
                      <div className="fh-panel-price">{p.price}</div>
                    </div>
                  ))}
                </div>
                <div className="fh-panel-footer">
                  <span className="fh-panel-footer-text">Freshness guaranteed daily</span>
                  <button
                    className="fh-btn fh-btn-primary"
                    style={{ padding: '0.45rem 1.1rem', fontSize: '0.8rem' }}
                    onClick={() => navigate('/products')}
                  >
                    View All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TRUST BAR ── */}
        <div className="fh-trust-bar">
          <div className="fh-container">
            <div className="fh-trust-items">
              <div className="fh-trust-item">
                <div className="fh-trust-icon" style={{ background: '#d1fae5' }}>
                  <BoltIcon style={{ width: '1rem', height: '1rem', color: '#059669' }} />
                </div>
                <span>Same-Day Delivery</span>
              </div>
              <div className="fh-trust-item">
                <div className="fh-trust-icon" style={{ background: '#dbeafe' }}>
                  <CurrencyRupeeIcon style={{ width: '1rem', height: '1rem', color: '#2563eb' }} />
                </div>
                <span>Cash & Online Payment</span>
              </div>
              <div className="fh-trust-item">
                <div className="fh-trust-icon" style={{ background: '#fef3c7' }}>
                  <CubeIcon style={{ width: '1rem', height: '1rem', color: '#d97706' }} />
                </div>
                <span>Fresh Stock Daily</span>
              </div>
              <div className="fh-trust-item">
                <div className="fh-trust-icon" style={{ background: '#fae8ff' }}>
                  <UserGroupIcon style={{ width: '1rem', height: '1rem', color: '#9333ea' }} />
                </div>
                <span>10k+ Customers</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── FEATURED PRODUCTS ── */}
        <section id="products" className="fh-section" style={{ background: 'white' }}>
          <div className="fh-container">
            <div className="fh-section-head">
              <span className="fh-section-badge fh-section-badge-green">🛒 OUR PRODUCTS</span>
              <h2 className="fh-section-title">Fresh From the Farm</h2>
              <p className="fh-section-sub">Hand-picked dairy products sourced directly from local farms. No middlemen, just freshness.</p>
            </div>
            <div className="fh-products-grid">
              {featuredProducts.map((p) => (
                <div key={p.name} className="fh-product-card" onClick={() => navigate('/products')}>
                  <div className="fh-product-img">
                    {p.badge && <span className="fh-product-badge-pill">{p.badge}</span>}
                    <span>{p.emoji}</span>
                  </div>
                  <div className="fh-product-body">
                    <div className="fh-product-name">{p.name}</div>
                    <div className="fh-product-desc">{p.desc}</div>
                    <div className="fh-product-pricing">
                      <span className="fh-product-price">{p.price}</span>
                      <span className="fh-product-original">{p.originalPrice}</span>
                    </div>
                    <div className="fh-card-actions">
                      <button
                        className={`fh-add-btn${addedMap[p.id] ? ' added' : ''}`}
                        onClick={(e) => handleAddToCart(p, e)}
                      >
                        {addedMap[p.id] ? '✓ Added!' : '+ Cart'}
                      </button>
                      <button
                        className="fh-buy-btn"
                        onClick={(e) => handleBuyNow(p, e)}
                      >
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center" style={{ marginTop: '2.5rem' }}>
              <button className="fh-btn fh-btn-primary" onClick={() => navigate('/products')}>
                View All Products
                <ArrowRightIcon style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>
          </div>
        </section>

        {/* ── WHY FRESH HILLS ── */}
        <section className="fh-usp-section">
          <div className="fh-container">
            <div className="fh-section-head">
              <span className="fh-section-badge fh-section-badge-green">⚡ WHY CHOOSE US</span>
              <h2 className="fh-section-title">More Than Just Fresh</h2>
              <p className="fh-section-sub">We bring the best of technology and trust for your daily grocery needs.</p>
            </div>
            <div className="fh-usp-grid">
              {[
                { icon: <TruckIcon />,       title: 'Fast Delivery',     desc: 'Same-day and scheduled delivery across Bangalore.' },
                { icon: <ClockIcon />,        title: '24/7 Shopping',     desc: 'Order anytime — our store never sleeps.' },
                { icon: <SparklesIcon />,     title: 'Farm to Doorstep',  desc: 'Direct from local farms, stocked twice daily.' },
                { icon: <GiftIcon />,         title: 'Rewards',           desc: 'Earn cashback and rewards on every order.' },
              ].map((u) => (
                <div key={u.title} className="fh-usp-card">
                  <div className="fh-usp-icon-wrap">{u.icon}</div>
                  <div className="fh-usp-title">{u.title}</div>
                  <div className="fh-usp-desc">{u.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── OFFERS ── */}
        <section id="offers" className="fh-section fh-offers-section">
          <div className="fh-container">
            <div className="fh-section-head">
              <span className="fh-section-badge fh-section-badge-amber">🎁 LIMITED OFFERS</span>
              <h2 className="fh-section-title">Today's Special Deals</h2>
              <p className="fh-section-sub">Grab these offers before they're gone! New deals added every week.</p>
            </div>
            <div className="fh-offers-grid">
              {offers.map((o, i) => (
                <div key={i} className="fh-offer-card">
                  <div className="fh-offer-icon-box">
                    {i === 0 && <GiftIcon />}
                    {i === 1 && <ClockIcon />}
                    {i === 2 && <ShoppingBagIcon />}
                  </div>
                  <div>
                    <div className="fh-offer-title">{o.title}</div>
                    <div className="fh-offer-discount">{o.discount}</div>
                    <span className="fh-offer-code">Code: {o.code}</span>
                    <div className="fh-offer-valid">{o.valid}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section id="how-it-works" className="fh-section" style={{ background: 'white' }}>
          <div className="fh-container">
            <div className="fh-section-head">
              <span className="fh-section-badge fh-section-badge-blue">⚡ SIMPLE PROCESS</span>
              <h2 className="fh-section-title">How It Works</h2>
              <p className="fh-section-sub">Three simple steps to fresh groceries at your door.</p>
            </div>
            <div className="fh-steps-grid">
              {[
                {
                  num: '1',
                  bg: 'linear-gradient(135deg,#10b981,#059669)',
                  title: 'Browse & Select',
                  desc: 'Visit our store, browse fresh dairy products and add your favourites to the cart.',
                },
                {
                  num: '2',
                  bg: 'linear-gradient(135deg,#06b6d4,#0891b2)',
                  title: 'Checkout & Pay',
                  desc: 'Place your order and pay via UPI, card, net banking, or cash on delivery.',
                },
                {
                  num: '3',
                  bg: 'linear-gradient(135deg,#8b5cf6,#7c3aed)',
                  title: 'Receive & Enjoy',
                  desc: 'We deliver farm-fresh products right to your doorstep. Guaranteed freshness!',
                },
              ].map((s) => (
                <div key={s.num} className="fh-step-card">
                  <div className="fh-step-num" style={{ background: s.bg }}>{s.num}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── STATS BANNER ── */}
        <section className="fh-stats-banner">
          <div className="fh-container">
            <div className="fh-stats-grid">
              {[
                { num: '50+',  lbl: 'Dairy Products' },
                { num: '10K+', lbl: 'Happy Families' },
                { num: '2x',   lbl: 'Faster Delivery' },
                { num: '24/7', lbl: 'Availability' },
              ].map((s) => (
                <div key={s.lbl}>
                  <div className="fh-stat-banner-num">{s.num}</div>
                  <div className="fh-stat-banner-lbl">{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="fh-section fh-testimonials-section">
          <div className="fh-container">
            <div className="fh-section-head">
              <span className="fh-section-badge fh-section-badge-purple">💬 CUSTOMER LOVE</span>
              <h2 className="fh-section-title">What Our Customers Say</h2>
              <p className="fh-section-sub">Join 10,000+ happy customers who trust Fresh Hills for their daily dairy needs.</p>
            </div>
            <div className="fh-testimonial-card">
              <div className="fh-testimonial-head">
                <div className="fh-testimonial-avatar">{testimonials[activeTestimonial].image}</div>
                <div>
                  <div className="fh-testimonial-name">{testimonials[activeTestimonial].name}</div>
                  <div className="fh-testimonial-loc">
                    <MapPinIcon style={{ width: '0.75rem', height: '0.75rem' }} />
                    {testimonials[activeTestimonial].location}
                  </div>
                  <div className="fh-stars">
                    {Array(5).fill(0).map((_, i) => <StarIcon key={i} />)}
                  </div>
                </div>
              </div>
              <p className="fh-testimonial-text">"{testimonials[activeTestimonial].text}"</p>
              <div className="fh-dots">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    className="fh-dot"
                    onClick={() => setActiveTestimonial(i)}
                    style={{ background: i === activeTestimonial ? '#059669' : '#d1d5db' }}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SUSTAINABILITY ── */}
        <section className="fh-section" style={{ background: 'white' }}>
          <div className="fh-container">
            <div className="fh-sustainability-grid">
              <div>
                <span className="fh-section-badge fh-section-badge-green">🌱 SUSTAINABILITY</span>
                <h2 className="fh-section-title" style={{ marginTop: '0.75rem' }}>
                  Freshness Without Compromise
                </h2>
                <p style={{ color: '#4b5563', lineHeight: '1.65', fontSize: '1.05rem' }}>
                  We partner with local dairy farms, reduce plastic waste, and use eco-friendly packaging. 
                  Every order contributes to a greener planet.
                </p>
                <div className="fh-sustainability-badges">
                  {[
                    { icon: <ShieldCheckIcon />, label: '100% Farm Fresh' },
                    { icon: <BoltIcon />,        label: 'Carbon Neutral' },
                    { icon: <HeartIcon />,        label: 'Ethical Sourcing' },
                  ].map((b) => (
                    <div key={b.label} className="fh-sus-badge">
                      {b.icon}{b.label}
                    </div>
                  ))}
                </div>
              </div>
              <div className="fh-sus-visual">
                <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🌍</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '900', color: '#065f46', marginBottom: '0.25rem' }}>1,200+ kg</h3>
                <p style={{ color: '#047857', fontWeight: '600' }}>Plastic saved since 2024</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── APP SECTION ── */}
        <section className="fh-app-section">
          <div className="fh-container">
            <div className="fh-app-grid">
              <div>
                <h2 className="fh-app-title">Get the Fresh Hills<br />Mobile App</h2>
                <p className="fh-app-text">
                  Shop faster, track orders in real time, and get exclusive app-only offers. 
                  Join 10,000+ users enjoying the convenience.
                </p>
                <div className="fh-app-btns">
                  <button className="fh-app-store-btn">
                    <DevicePhoneMobileIcon />
                    App Store
                  </button>
                  <button className="fh-app-store-btn">
                    <DevicePhoneMobileIcon />
                    Google Play
                  </button>
                </div>
              </div>
              <div style={{ display: 'none' }} className="lg-block">
                <div className="fh-app-visual">
                  <div className="fh-app-inner">
                    <div className="fh-app-emojis">
                      <span>🥛</span><span>🧈</span><span>🧀</span>
                    </div>
                    <p style={{ fontWeight: '700', color: '#111827', fontSize: '1.1rem', marginBottom: '0.25rem' }}>Fresh Hills</p>
                    <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Your daily dairy companion</p>
                    <button
                      className="fh-btn fh-btn-primary"
                      style={{ marginTop: '1.25rem', width: '100%', justifyContent: 'center' }}
                      onClick={() => navigate('/products')}
                    >
                      Shop Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer id="contact" className="fh-footer">
          <div className="fh-container">
            <div className="fh-footer-grid">
              {/* Brand */}
              <div>
                <div className="fh-footer-logo">
                  <img src="/images/logo.png" alt="Fresh Hills"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }}
                  />
                  <h3>Fresh Hills</h3>
                </div>
                <p className="fh-footer-about">
                  India's trusted online dairy store. Fresh milk, eggs, butter and more — 
                  delivered to your door every morning since 2024.
                </p>
                <div className="fh-social-row">
                  {['fb','tw','ig'].map((s) => (
                    <a key={s} href="#" className="fh-social-btn">
                      <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '1rem', height: '1rem' }}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="fh-footer-col-title">
                  <HeartIcon style={{ width: '1rem', height: '1rem', color: '#ef4444' }} />
                  Quick Links
                </h4>
                <ul className="fh-footer-links">
                  {[
                    { label: 'Home',         href: '#home',         route: null },
                    { label: 'Products',     href: null,            route: '/products' },
                    { label: 'Offers',       href: '#offers',       route: null },
                    { label: 'How It Works', href: '#how-it-works', route: null },
                    { label: 'Contact',      href: '#contact',      route: null },
                  ].map((l) => (
                    <li key={l.label}>
                      {l.route
                        ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); navigate(l.route); }}>
                            <ChevronRightIcon style={{ width: '0.7rem' }} />{l.label}
                          </a>
                        ) : (
                          <a href={l.href}>
                            <ChevronRightIcon style={{ width: '0.7rem' }} />{l.label}
                          </a>
                        )
                      }
                    </li>
                  ))}
                </ul>
              </div>

              {/* Products */}
              <div>
                <h4 className="fh-footer-col-title">
                  <HeartIcon style={{ width: '1rem', height: '1rem', color: '#ef4444' }} />
                  Our Products
                </h4>
                <ul className="fh-footer-links">
                  {['Fresh Milk','Butter & Cream','Cheese','Farm Eggs','Curd & Yogurt'].map((p) => (
                    <li key={p}>
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate('/products'); }}>
                        <ChevronRightIcon style={{ width: '0.7rem' }} />{p}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="fh-footer-col-title">
                  <HeartIcon style={{ width: '1rem', height: '1rem', color: '#ef4444' }} />
                  Contact Us
                </h4>
                <ul className="fh-footer-contact-list">
                  <li>
                    <MapPinIcon />
                    <span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Thrikkakara, Ernakulam, Kerala, 682021</span>
                  </li>
                  <li>
                    <DevicePhoneMobileIcon />
                    <span>+91 70108 44160</span>
                  </li>
                  <li>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '1.1rem', height: '1.1rem', flexShrink: 0 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>hello@fesiconenterprises.com</span>
                  </li>
                </ul>
                <div className="fh-newsletter">
                  <div className="fh-newsletter-label">Subscribe for offers</div>
                  <div className="fh-newsletter-row">
                    <input type="email" placeholder="Your email address" />
                    <button className="fh-newsletter-btn">Go</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom */}
            <div className="fh-footer-bottom">
              <div className="fh-copyright">
                © {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in India
                <br />
                <span style={{ fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>
                  GST: 32AAGFF6427Q1Z2
                </span>
              </div>
              <div className="fh-footer-bottom-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="#">Cookie Policy</a>
                <a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="fh-seo-link">
                  SEO9INE <HeartIcon className="animate-heart" style={{ width: '0.85rem', height: '0.85rem', color: '#ef4444' }} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;